import { useMutation, gql } from "@apollo/client";
import useEnsureClientHasCart from "./useEnsureClientHasCart";
import { CartItemFragment } from "../components/cart/CartItem";
import usePermissions from "./usePermissions";

const AddCartProductMutation = gql`
  mutation AddCartProduct(
    $orderId: ID
    $productId: ID!
    $configuration: [ProductConfigurationParameterInput!]
    $quantity: Int
  ) {
    addCartProduct(
      orderId: $orderId
      productId: $productId
      configuration: $configuration
      quantity: $quantity
    ) {
      ...CartItemFragment
      order {
        _id
        user {
          _id
          cart {
            _id
            items {
              ...CartItemFragment
            }
          }
          self {
            _id
            cart {
              _id
              items {
                ...CartItemFragment
              }
            }
          }
        }
      }
    }
  }
  ${CartItemFragment}
`;

const useAddCartProduct = (clientId?: string) => {
  const [addCartProductMutation] = useMutation(AddCartProductMutation);
  const { canOrderClientsProducts } = usePermissions();
  const { orderId } = useEnsureClientHasCart(clientId);

  if (!orderId) {
    return {
      addCartProduct: null,
    };
  }

  const addCartProduct = async ({
    product,
    quantity,
    configuration,
  }: {
    product?: {
      sku: string;
      _id: string;
    };
    quantity: number;
    configuration?: any;
  }) => {
    const hasPermission = await canOrderClientsProducts(clientId, product?.sku);
    if (!hasPermission) return null;
    const result = await addCartProductMutation({
      variables: {
        orderId,
        productId: product?._id,
        quantity: parseInt(quantity as unknown as string, 10),
        configuration: configuration || undefined,
      },
      refetchQueries: ["ClientCart", "Client", "OwnClients", "CurrentUser"],
      awaitRefetchQueries: true,
    });
    return result;
  };

  return {
    addCartProduct,
  };
};

export default useAddCartProduct;
